import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import styles from './AccountingDashboardView.module.css';
import AppLayout from '../components/AppLayout';
import { getAllDeals } from '../api/customerApi';

const translations = {
  en: {
    pageTitle: 'Accounting Dashboard',
    welcomeMessage:
      'Manage product prices, invoices, payments, and financial reports from here.',
    priceListTitle: 'Price List',
    priceListText: 'Set and review product pricing.',
    invoicesTitle: 'Invoices & Payments',
    invoicesText: 'Record payments, track totals, and manage deal payment status.',
    reportsTitle: 'Reports',
    reportsText: 'Review daily, weekly, monthly, and yearly results.',
    paymentDueTitle: 'Payment attention needed',
    paymentDueText: 'postpaid invoice payment date has arrived or passed.',
    paymentDueTextPlural: 'postpaid invoice payment dates have arrived or passed.',
    viewInvoices: 'View invoices',
    comingSoon: 'Coming soon',
    open: 'Open',
    logout: 'Logout',
  },
  ar: {
    pageTitle: 'لوحة المحاسبة',
    welcomeMessage:
      'أدر أسعار المنتجات والفواتير والمدفوعات والتقارير المالية من هنا.',
    priceListTitle: 'قائمة الأسعار',
    priceListText: 'حدد وراجع أسعار المنتجات.',
    invoicesTitle: 'الفواتير والمدفوعات',
    invoicesText: 'سجّل المدفوعات وتابع الإجماليات وأدر حالة الدفع للصفقات.',
    reportsTitle: 'التقارير',
    reportsText: 'راجع النتائج اليومية والأسبوعية والشهرية والسنوية.',
    paymentDueTitle: 'تنبيه مدفوعات مستحقة',
    paymentDueText: 'فاتورة آجلة وصل أو تجاوز موعد دفعها.',
    paymentDueTextPlural: 'فواتير آجلة وصل أو تجاوز موعد دفعها.',
    viewInvoices: 'عرض الفواتير',
    comingSoon: 'قريباً',
    open: 'فتح',
    logout: 'تسجيل الخروج',
  },
};

const AccountingDashboardView = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const navigate = useNavigate();
  const t = (key) => translations[language][key];
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    let isMounted = true;

    getAllDeals()
      .then(({ data }) => {
        if (isMounted) {
          setDeals(Array.isArray(data) ? data : []);
        }
      })
      .catch((error) => {
        console.error('Error loading accounting payment alerts:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const duePaymentDeals = useMemo(() => {
    const now = new Date();
    return deals.filter((row) => {
      const deal = row.deal || {};
      if (deal.paymentType !== 'postpaid' || deal.paymentStatus === 'paid' || !deal.paymentDueDate) {
        return false;
      }
      const dueDate = new Date(deal.paymentDueDate);
      if (Number.isNaN(dueDate.getTime())) return false;
      dueDate.setHours(0, 0, 0, 0);
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return dueDate <= today;
    });
  }, [deals]);

  const sections = [
    {
      title: t('priceListTitle'),
      text: t('priceListText'),
      action: () => navigate('/accounting/product-prices'),
      ready: true,
    },
    {
      title: t('invoicesTitle'),
      text: t('invoicesText'),
      action: () => navigate('/accounting/deals'),
      ready: true,
    },
    {
      title: t('reportsTitle'),
      text: t('reportsText'),
      action: () => navigate('/accounting/deals'),
      ready: true,
    },
  ];

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={() => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'))}
      showLogout
      logoutLabel={t('logout')}
      contentClassName={styles.dashboard}
    >
      <section className={styles.intro}>
        <p>{t('welcomeMessage')}</p>
      </section>

      {duePaymentDeals.length > 0 && (
        <section className={styles.paymentWarning} role="alert">
          <div>
            <strong>{t('paymentDueTitle')}</strong>
            <p>
              {duePaymentDeals.length}{' '}
              {duePaymentDeals.length === 1 ? t('paymentDueText') : t('paymentDueTextPlural')}
            </p>
          </div>
          <button type="button" onClick={() => navigate('/accounting/deals')}>
            {t('viewInvoices')}
          </button>
        </section>
      )}

      <section className={styles.sectionGrid}>
        {sections.map((section) => (
          <article key={section.title} className={styles.sectionCard}>
            <div>
              <h2>{section.title}</h2>
              <p>{section.text}</p>
            </div>
            {section.ready ? (
              <button
                type="button"
                className={styles.openButton}
                onClick={section.action}
              >
                {t('open')}
              </button>
            ) : (
              <span className={styles.statusBadge}>{t('comingSoon')}</span>
            )}
          </article>
        ))}
      </section>
    </AppLayout>
  );
};

export default AccountingDashboardView;
